import Image from 'next/image';

import { Box, Flex, Heading, SimpleGrid, Stack, Text, VisuallyHidden } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { GlobeIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  ImpactDrivenCountry,
  impactDrivenSolutionsContent,
  impactDrivenSolutionsCountries,
  impactDrivenSolutionsLockupAssets,
  impactDrivenSolutionsStats,
  impactDrivenSolutionsStripesSrc,
  ImpactDrivenStat,
  ImpactDrivenStatTheme,
} from '~/features/government/configs/impact-driven-solutions-content';

// All four theme colors exist as Chakra tokens in `src/styles/theme/v2.ts` — blue.500
// (#175CFF), blue.700 (#00298C), purple.500 (#931CF5), purple.700 (#6100B0) — so we
// avoid hardcoded hex. `valueColor` drives the icon + numeric value + valueSuffix color
// and is also used as the divider rail's top stop (fades to transparent at the bottom).
type StatTokens = { valueColor: string };

const STAT_TOKENS: Record<ImpactDrivenStatTheme, StatTokens> = {
  blue700: { valueColor: 'blue.700' },
  blue500: { valueColor: 'blue.500' },
  purple700: { valueColor: 'purple.700' },
  purple500: { valueColor: 'purple.500' },
};

// Gradient on the dark "Trusted by" panel matches Figma nodes 159:24761 (desktop) and
// 159:25253 (mobile). Mobile angle is ~29.5°; desktop is ~62.4° — close enough that a
// single diagonal gradient reads correctly at both breakpoints.
const TRUSTED_BY_GRADIENT = 'linear-gradient(108deg, #031643 0%, #00298C 50%, #6100B0 100%)';

export type GovernmentImpactDrivenSolutionsProps = {
  pretitle?: string;
  heading?: string;
  intro?: string;
  trustedByHeading?: string;
  globalFootprintHeading?: string;
  globalFootprintIntro?: string;
  logoLockupAlt?: string;
  statsFootnote?: string;
  stats?: ImpactDrivenStat[];
  countries?: ImpactDrivenCountry[];
};

export function GovernmentImpactDrivenSolutions({
  pretitle = impactDrivenSolutionsContent.pretitle,
  heading = impactDrivenSolutionsContent.heading,
  intro = impactDrivenSolutionsContent.intro,
  trustedByHeading = impactDrivenSolutionsContent.trustedByHeading,
  globalFootprintHeading = impactDrivenSolutionsContent.globalFootprintHeading,
  globalFootprintIntro = impactDrivenSolutionsContent.globalFootprintIntro,
  logoLockupAlt = impactDrivenSolutionsContent.logoLockupAlt,
  statsFootnote = impactDrivenSolutionsContent.statsFootnote,
  stats = impactDrivenSolutionsStats,
  countries = impactDrivenSolutionsCountries,
}: GovernmentImpactDrivenSolutionsProps) {
  return (
    <Box as='section' bg='grey.50' pb={{ base: '0', md: '96px' }} pt={{ base: '56px', md: '96px' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          direction='column'
          gap={{ base: '40px', md: '96px' }}
          margin='0 auto'
          maxW={{ base: '100%', md: '1056px' }}
        >
          <Flex direction='column' gap={{ base: '24px', md: '64px' }} w='100%'>
            <SectionHeader
              color='grey.500'
              containerProps={{ alignItems: 'center', margin: '0 auto', w: '100%' }}
              iconSlot={<GlobeIcon boxSize={{ base: '14px', md: '18px' }} color='grey.500' />}
              subtitle={pretitle}
              descriptionSlot={
                <Text
                  color='grey.500'
                  fontSize={{ base: '0.875rem', md: '1rem' }}
                  letterSpacing={{ base: '-0.01em', md: 'normal' }}
                  lineHeight={{ base: '1.4', md: '1.5' }}
                  maxW={{ base: '324px', md: '810px' }}
                >
                  {intro}
                </Text>
              }
              titleSlot={
                <Heading
                  as='h2'
                  color='grey.900'
                  fontFamily='heading'
                  fontSize={{ base: '2.375rem', md: '3.375rem' }}
                  fontWeight='600'
                  letterSpacing='-0.02em'
                  lineHeight='1.1'
                  maxW={{ base: '100%', md: '992px' }}
                >
                  {heading}
                </Heading>
              }
            />
            <Flex direction='column' gap={{ base: '16px', md: '24px' }} w='100%'>
              <StatsRow stats={stats} />
              {statsFootnote ? (
                <Text
                  color='grey.500'
                  fontSize={{ base: '0.625rem', md: '0.6875rem' }}
                  lineHeight='1.4'
                  textAlign='center'
                >
                  <Box as='sup' color='grey.900'>
                    *
                  </Box>
                  {statsFootnote}
                </Text>
              ) : null}
            </Flex>
          </Flex>

          <Flex direction='column' gap={{ base: '0', md: '40px' }} w='100%'>
            <TrustedByPanel heading={trustedByHeading} logoLockupAlt={logoLockupAlt} />
            <GlobalFootprintPanel countries={countries} heading={globalFootprintHeading} intro={globalFootprintIntro} />
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function StatsRow({ stats }: { stats: ImpactDrivenStat[] }) {
  // Breakpoints: base (mobile, single column), md (≥768px tablet — 4-up with compressed
  // sizing so cards don't overflow), lg (≥992px desktop — full Figma sizing). Without the
  // `md` step, the desktop 54px values + 32px padding wouldn't fit at 768px.
  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} gap={{ base: '8px', md: '12px', lg: '24px' }} w='100%'>
      {stats.map((stat) => (
        <StatCard key={stat.id} stat={stat} />
      ))}
    </SimpleGrid>
  );
}

function StatCard({ stat }: { stat: ImpactDrivenStat }) {
  const tokens = STAT_TOKENS[stat.theme];
  const { Icon } = stat;

  // PercentIcon has an 18×18 viewBox (its glyph fills ~96% of the viewBox) while the
  // other three stat icons use 20×20 viewBoxes with glyphs filling ~83% — so at the
  // same boxSize, PercentIcon renders visually larger. Shrink it to ~21px to match
  // siblings optically (21 × 18/20 × 96/83 ≈ 21.8 — within a pixel of optical parity).
  const iconBoxSize = stat.id === 'advanced' ? '21px' : '24px';

  return (
    <Box
      bg='white'
      border='1px solid'
      borderColor='grey.100'
      borderRadius={{ base: '6px', md: '6px', lg: '8px' }}
      p={{ base: '20px', md: '20px', lg: '32px' }}
    >
      {/* The left rail is a top-to-bottom gradient (theme color → transparent) rather than
          a solid border. Implemented as an absolute-positioned 2px-wide Box so the gradient
          renders correctly (CSS `border-image` is fiddly across browsers; an absolute Box
          with `backgroundImage` is robust). Color matches the stat's value color (Blue-700,
          Blue-500, Purple-700, Purple-500) per Figma node 159:24733 — the lighter tones
          exposed in the Figma "border-l-2" tailwind export (#7BA2FF / #E5A5FF) are a Figma
          tailwind-export artifact; the rendered design uses the value color. */}
      <Box pl={{ base: '16px', md: '12px', lg: '16px' }} position='relative'>
        <Box
          bgGradient={`linear(to-b, ${tokens.valueColor}, transparent)`}
          bottom={0}
          left={0}
          position='absolute'
          top={0}
          w='2px'
        />
        <Stack spacing={{ base: '16px', md: '12px', lg: '16px' }}>
          {/* Mobile: value left, icon right on a single row. Tablet+desktop: icon stacked
              above value. */}
          <Flex
            align='flex-start'
            direction={{ base: 'row', md: 'column' }}
            gap={{ base: '0', md: '4px', lg: '8px' }}
            justify={{ base: 'space-between', md: 'flex-start' }}
            w='100%'
          >
            {/* Icon slot — render the icon if matched, else a layout-preserving spacer
                (order swapped between mobile and desktop via `order`). */}
            <Flex
              align='center'
              color={tokens.valueColor}
              flexShrink={0}
              h='24px'
              justify={{ base: 'flex-end', md: 'flex-start' }}
              order={{ base: 2, md: 1 }}
              w='24px'
            >
              {Icon ? <Icon boxSize={iconBoxSize} /> : null}
            </Flex>
            <Flex align='baseline' order={{ base: 1, md: 2 }}>
              <Text
                as='span'
                color={tokens.valueColor}
                fontFamily='heading'
                fontSize={{ base: '3rem', md: '2.5rem', lg: '3.375rem' }}
                fontWeight='500'
                letterSpacing='-0.08em'
                lineHeight={{ base: '40px', md: '36px', lg: '44px' }}
              >
                {stat.value}
              </Text>
              <Text
                as='span'
                color={tokens.valueColor}
                fontFamily='heading'
                fontSize={{ base: '1.875rem', md: '1.5rem', lg: '2rem' }}
                fontWeight='500'
                lineHeight={{ base: '40px', md: '36px', lg: '44px' }}
                ml='4px'
              >
                {stat.valueSuffix}
              </Text>
            </Flex>
          </Flex>
          <Text color='grey.900' fontSize={{ base: '0.75rem', md: '0.75rem', lg: '0.8125rem' }} lineHeight='1.4'>
            {stat.caption}
            {stat.disclaimer ? (
              <Box as='sup' color='grey.900' fontWeight='700'>
                {stat.disclaimer}
              </Box>
            ) : null}
          </Text>
        </Stack>
      </Box>
    </Box>
  );
}

function TrustedByPanel({ heading, logoLockupAlt }: { heading: string; logoLockupAlt: string }) {
  return (
    <Box
      backgroundImage={TRUSTED_BY_GRADIENT}
      borderRadius={{ base: '0', md: '12px' }}
      mx={{ base: '-16px', md: '0' }}
      overflow='hidden'
      position='relative'
      px={{ base: '16px', md: '56px' }}
      py={{ base: '48px', md: '48px' }}
      w={{ base: 'calc(100% + 32px)', md: '100%' }}
    >
      <Stack position='relative' spacing={{ base: '24px', md: '40px' }} zIndex={1}>
        <Box borderBottom='1px solid' borderColor='whiteAlpha.300' pb={{ base: '24px', md: '32px' }} w='100%'>
          <Heading
            as='h3'
            color='white'
            fontFamily='heading'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={{ base: '1.15', md: '1.1' }}
            textAlign={{ base: 'center', md: 'center' }}
          >
            {heading}
          </Heading>
        </Box>
        <Box display={{ base: 'none', md: 'block' }} position='relative' w='100%'>
          <Image
            alt={logoLockupAlt}
            height={216}
            sizes='(min-width: 48em) 944px, 100vw'
            src={impactDrivenSolutionsLockupAssets.desktopSrc}
            style={{ width: '100%', height: 'auto' }}
            width={944}
          />
        </Box>
        <Box display={{ base: 'block', md: 'none' }} position='relative' w='100%'>
          <Image
            alt={logoLockupAlt}
            height={322}
            sizes='100vw'
            src={impactDrivenSolutionsLockupAssets.mobileSrc}
            style={{ width: '100%', height: 'auto' }}
            width={358}
          />
        </Box>
      </Stack>

      <Box
        h={{ base: '200px', md: '280px' }}
        mixBlendMode='overlay'
        pointerEvents='none'
        position='absolute'
        right='0'
        top='0'
        w={{ base: '200px', md: '280px' }}
        zIndex={0}
      >
        <Image
          alt=''
          src={impactDrivenSolutionsStripesSrc}
          style={{ objectFit: 'contain', objectPosition: 'top right' }}
          fill
        />
      </Box>
    </Box>
  );
}

function GlobalFootprintPanel({
  countries,
  heading,
  intro,
}: {
  countries: ImpactDrivenCountry[];
  heading: string;
  intro: string;
}) {
  return (
    <Box
      bg='white'
      border={{ base: 'none', md: '1px solid' }}
      borderColor={{ md: 'grey.100' }}
      borderRadius={{ base: '0', md: '12px' }}
      mx={{ base: '-16px', md: '0' }}
      overflow='hidden'
      position='relative'
      px={{ base: '16px', md: '56px' }}
      py={{ base: '48px', md: '48px' }}
      w={{ base: 'calc(100% + 32px)', md: '100%' }}
    >
      <Stack position='relative' spacing={{ base: '32px', md: '48px' }} zIndex={1}>
        <Stack
          borderBottom='1px solid'
          borderColor='grey.200'
          pb={{ base: '24px', md: '24px' }}
          spacing={{ base: '16px', md: '12px' }}
          textAlign={{ base: 'center', md: 'left' }}
          w='100%'
        >
          <Heading
            as='h3'
            color='grey.900'
            fontFamily='heading'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={{ base: '1.15', md: '1.1' }}
          >
            {heading}
          </Heading>
          <Text
            color='grey.500'
            fontSize={{ base: '0.875rem', md: '1rem' }}
            letterSpacing={{ base: '-0.01em', md: 'normal' }}
            lineHeight={{ base: '1.4', md: '1.5' }}
          >
            {intro}
          </Text>
        </Stack>
        <CountryGrid countries={countries} />
      </Stack>
    </Box>
  );
}

function CountryGrid({ countries }: { countries: ImpactDrivenCountry[] }) {
  return (
    <SimpleGrid
      as='ul'
      columnGap={{ base: '13px', md: '16px' }}
      columns={{ base: 4, md: 5 }}
      justifyItems='center'
      listStyleType='none'
      m={0}
      p={0}
      rowGap={{ base: '32px', md: '40px' }}
      w='100%'
    >
      {countries.map((country) => (
        <CountryTile key={country.id} country={country} />
      ))}
    </SimpleGrid>
  );
}

function CountryTile({ country }: { country: ImpactDrivenCountry }) {
  // The supplied flag SVGs are Figma exports with a built-in drop shadow baked
  // into the lower half of their 126×139 viewBox (flag tile in the upper portion
  // at viewBox x=23..103, y=4..58). The wrapper Box uses `minH='78px'` so the
  // absolutely-positioned `<Image fill>` has a sized ancestor to fill; the image
  // uses `objectFit='cover'` + `objectPosition='top'` so it scales to 88px wide
  // and anchors to the top of the box. The country label sits absolutely-
  // positioned at the bottom of the same box, layered over whatever portion of
  // the SVG's shadow padding falls below the flag tile.
  return (
    <Stack align='center' as='li' listStyleType='none' position='relative' w={{ base: '80px', md: '88px' }}>
      <Box
        overflow='hidden'
        position='relative'
        _after={{
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
          bottom: 0,
          content: '""',
          height: '40%',
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          right: 0,
          zIndex: 1,
        }}
        minH={{
          base: '64px',
          md: '94px',
        }}
        width={{
          base: '80px',
          md: '124px',
        }}
      >
        <Image
          alt={country.flagAlt}
          sizes='88px'
          src={country.flagSrc}
          style={{ objectFit: 'cover', objectPosition: 'top' }}
          fill
        />
      </Box>
      <Text
        bottom={0}
        color='grey.500'
        fontFamily='condensed'
        fontSize={{ base: '0.8125rem', md: '0.9375rem' }}
        fontWeight='500'
        left='50%'
        letterSpacing={{ base: '1.04px', md: '0.9px' }}
        lineHeight='1'
        position='absolute'
        textAlign='center'
        textTransform='uppercase'
        transform='translateX(-50%)'
        whiteSpace='nowrap'
        zIndex={2}
      >
        {/* Visible-only fragments: aria-hidden so SR ignores them and only reads the
            VisuallyHidden full name below. The mobile span uses `shortLabel` if Figma
            abbreviates this country, otherwise falls back to the full label. */}
        <Box as='span' display={{ base: 'inline', md: 'none' }} aria-hidden>
          {country.shortLabel ?? country.label}
        </Box>
        <Box as='span' display={{ base: 'none', md: 'inline' }} aria-hidden>
          {country.label}
        </Box>
        {/* Always-announced full name — SR reads each country exactly once at both
            breakpoints; the visual spans above are hidden from the accessibility tree. */}
        <VisuallyHidden>{country.label}</VisuallyHidden>
      </Text>
    </Stack>
  );
}
